import {
  CartItemFragment,
  GetCartQuery,
  CartItemParamInput,
  CartItemResultFragment,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { flatten, getRowPrice } from '@dustin-web/microsite-data/src/utils'
import { useUserSettings } from '@dustin-web/microsite-components/src/hooks'
import {
  makeVar,
  useReactiveVar,
} from '@dustin-web/microsite-components/src/hooks/use-reactive-var'
import { use, useCallback, useMemo } from 'react'
import debounce from 'lodash/debounce'
import {
  getOptimisticResponse,
  upsertCartCacheWithNewItems,
} from '@dustin-web/product-components/src/hooks/cart/cache-updater-cart'
import { CartBaseProduct } from '@dustin-web/product-components/src/hooks/cart/cart-base-product-types'
import {
  addMultipleProductsToCartAction,
  addProductToCartAction,
  emptyCartAction,
} from '@dustin-web/server-components/src/components/cart/add-to-cart-action'
import type { UseShoppingCartProps } from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import { useSWR } from './use-graphql-swr'
import {
  CartActionType,
  useAssistiveCartContext,
} from '@dustin-web/microsite-components/src/hooks/use-assistive-cart-context'
import getCartAction from '@dustin-web/microsite-data/src/actions/get-cart-action'
import { getCacheKeyFromDocument } from './graphql-fetcher'
import { GET_CART } from '../graphql/product/get-customer-product-lists.graphql'
import { Middleware, SWRHook, useSWRConfig } from 'swr'

export type UpdateCart = ReturnType<typeof useShoppingCartSWR>['updateCart']
export type UpdateCartWithMultipleProducts = ReturnType<
  typeof useShoppingCartSWR
>['updateCartWithMultipleProducts']
export type EmptyCart = ReturnType<typeof useShoppingCartSWR>['emptyCart']
export type CartProduct = NonNullable<CartItemFragment['product']>
export type UpdateCartWithMultipleProductParams = CartItemParamInput & { product: CartBaseProduct }

const isUpdateVar = makeVar(false)

const filterAvailableCartItems = (cartItems: CartItemFragment[]) => {
  return cartItems.filter(x => x.product?.availability?.isAvailableForSale || x.isMandatory)
}

const lastUpdatedProductErpIdentifierVar = makeVar('')

const resolveReactPromise: Middleware = (useSWRNext: SWRHook) => (key, fetcher, config) => {
  const { fallback } = useSWRConfig()
  const cacheKey = getCacheKeyFromDocument(GET_CART)
  const cachedData = fallback[cacheKey] as GetCartQuery | Promise<GetCartQuery> | undefined
  if (typeof cachedData === 'object' && cachedData instanceof Promise) {
    const data = use(cachedData)
    return useSWRNext(key, fetcher, { ...config, fallbackData: data as any })
  }
  return useSWRNext(key, fetcher, config)
}

export const useShoppingCartSWR = (options: UseShoppingCartProps = {}) => {
  const { withRequiredEndUserInformation = false } = options
  const { showPricesInclVat } = useUserSettings()
  const isUpdating = useReactiveVar(isUpdateVar)
  const lastUpdatedProductErpIdentifier = useReactiveVar(lastUpdatedProductErpIdentifierVar)
  const { setAssistiveCartData } = useAssistiveCartContext()

  const {
    data: cartData,
    error,
    isValidating: loading,
    mutate,
  } = useSWR<GetCartQuery>(
    getCacheKeyFromDocument(GET_CART),
    () => getCartAction(withRequiredEndUserInformation),
    { revalidateOnMount: false, use: [resolveReactPromise] }
  )

  const cartItemsCacheKey = JSON.stringify(cartData?.cart?.items) + loading

  const cartItems = useMemo(
    () => filterAvailableCartItems(cartData?.cart?.items || []),
    [cartItemsCacheKey]
  )

  const priceSummaries = useMemo(
    () =>
      cartItems.reduce(
        (acc, item) => {
          const totalPrice =
            acc.totalPrice +
            (item.product?.price
              ? getRowPrice(item.product.price, showPricesInclVat, item.quantity)
              : 0)
          const totalPriceExcludingVat =
            acc.totalPriceExcludingVat +
            (item.product?.price?.priceExcludingVat ?? 0) * item.quantity
          const totalPriceIncludingVat =
            acc.totalPriceIncludingVat +
            (item.product?.price?.priceIncludingVat ?? 0) * item.quantity

          const totalVat = totalPriceIncludingVat - totalPriceExcludingVat

          return {
            totalPrice,
            totalPriceExcludingVat,
            totalPriceIncludingVat,
            totalVat,
          }
        },
        { totalPrice: 0, totalPriceExcludingVat: 0, totalPriceIncludingVat: 0, totalVat: 0 }
      ),
    [cartItemsCacheKey]
  )

  const totalQuantity = useMemo(
    () => cartItems.reduce((acc, val) => acc + val.quantity, 0),
    [cartItems]
  )

  const updateCartWithMultipleProducts = async (
    cartItemParams: UpdateCartWithMultipleProductParams[]
  ) => {
    const params: CartItemParamInput[] = cartItemParams.map(x => ({
      ...x,
      product: undefined,
    }))

    const optimisticResponseData = flatten(
      cartItemParams.map(x =>
        getOptimisticResponse({
          cartItems,
          productErpIdentifier: x.productErpIdentifier || '',
          mainProductErpIdentifier: x.mainProductErpIdentifier || null,
          quantity: x.quantity,
          product: x.product,
          comment: x.comment,
        })
      )
    )

    setAssistiveCartData({
      type: CartActionType.MULTIPLEPRODUCTS,
      actionData: { cartItems: cartItems, numberOfProducts: cartItemParams.length },
    })

    isUpdateVar(true)

    // Optimistic response
    mutate(updateCartCache(cartData?.cart, optimisticResponseData, true), false)

    await addMultipleProductsToCartAction({
      cartItemParams: params,
      withRequiredEndUserInformation,
    }).catch(err => {
      isUpdateVar(false)
      throw err
    })

    isUpdateVar(false)

    // Refetch to ensure latest data
    mutate()
  }

  const debounces = {} as { [key: string]: any }

  const debouncedUpdateCartMutation = useCallback((key: string) => {
    if (!debounces[key]) {
      debounces[key] = debounce(async (variables?: any) => {
        // @ts-ignore
        const response = await addProductToCartAction(variables).catch(err => {
          isUpdateVar(false)
          throw err
        })

        isUpdateVar(false)

        setAssistiveCartData({
          type: CartActionType.SINGLEPRODUCT,
          actionData: {
            cartItems: cartItems,
            product: {
              productErpIdentifier: variables.productErpIdentifier,
              displayName: variables.productName,
              newQuantity: variables.quantity,
            },
          },
        })

        // Refetch to ensure latest data
        mutate()
      }, 1000)
    }

    return debounces[key]
  }, [])

  const updateCartCache = (
    cart: GetCartQuery['cart'],
    newData: CartItemResultFragment[],
    preventSetLastUpdatedProductErpIdentifier?: boolean
  ) => {
    const data = upsertCartCacheWithNewItems(cart, newData)

    if (!preventSetLastUpdatedProductErpIdentifier) {
      lastUpdatedProductErpIdentifierVar(
        newData
          .filter(p => !p.parentCartItemId && p.quantity > 0)
          .map(p => p.productErpIdentifier)[0]
      )
    }

    return data
  }

  const updateCart = async (
    product: CartBaseProduct,
    mainProductErpIdentifier: string | null,
    newQuantity: number,
    comment?: string | null,
    parentCartItemId?: number | null,
    preventSetLastUpdatedProductErpIdentifier?: boolean
  ) => {
    const actualQuantity = newQuantity < 0 ? 0 : newQuantity

    const variables = {
      productErpIdentifier: product.productErpIdentifier,
      mainProductErpIdentifier,
      quantity: actualQuantity,
      comment,
      withRequiredEndUserInformation,
      parentCartItemId: parentCartItemId || null,
      preventSetLastUpdatedProductErpIdentifier,
      productName: product.displayName,
    }

    const optimisticResponseData = getOptimisticResponse({
      cartItems,
      ...variables,
      product,
    })

    isUpdateVar(true)

    // Optimistic response
    mutate(
      updateCartCache(
        cartData?.cart,
        optimisticResponseData,
        preventSetLastUpdatedProductErpIdentifier
      ),
      false
    )

    await debouncedUpdateCartMutation(product.productErpIdentifier)(variables)
  }

  const emptyCart = () => {
    const cartWithoutItems = {
      cart: {
        id: cartData?.cart?.id || 0,
        items: [],
      },
    }

    setAssistiveCartData({
      type: CartActionType.CLEARCART,
      actionData: {},
    })

    mutate(
      async () => {
        await emptyCartAction()
        return cartWithoutItems
      },
      { revalidate: true, optimisticData: cartWithoutItems, populateCache: false }
    )
  }

  /** Get the quantity of a product in the cart. Returns 0 if product is not in cart */
  const quantityOfProductInCart = (productErpIdentifier: string): number => {
    return (
      cartItems
        .filter(i => i?.product?.productErpIdentifier === productErpIdentifier)
        .reduce((a, b) => a + b.quantity, 0) || 0
    )
  }

  const data = {
    loading,
    error,
    cartItems,
    quantityOfProductInCart,
    updateCart,
    updateCartWithMultipleProducts,
    emptyCart,
    totalQuantity,
    lastUpdatedProductErpIdentifier,
    isUpdating,
    refetchCart: mutate,
    suggestedDelivery: cartData?.suggestedDelivery,
    ...priceSummaries,
  }

  return data
}

export default useShoppingCartSWR
